import React, { useRef, useState } from 'react';
import { ProgressBar, Table, Badge } from 'react-bootstrap';
import styles from './SchemaTemplatesCSVTool.module.css';
import ValidationFeedback from '../../components/ValidationFeedback/ValidationFeedback';
import {
  UpdateSchemaTemplatesBatch,
  CreateSchemaTemplatesBatch,
} from '../../libs/GraphQL';
import {
  buildUpdateInputFromRows,
  buildUpdateInputRow,
  normalizeDays,
  isValidTimeHHMM,
} from './SchemaTemplatesUtils';

const VALIDATION_TYPE = {
  ERRORS: 'errors',
  WARNINGS: 'warnings',
  SUCCESS: 'success',
};

const REQUIRED_HEADERS = [
  'schemaId',
  'schemaType',
  'discount',
  'discountSchedule',
  'discountUI',
  'expiringDays',
  'priceDisplayType',
  'platform',
];

// Additional required headers for Create mode
const CREATE_MODE_REQUIRED_HEADERS = [
  ...REQUIRED_HEADERS,
  'replicaSchemaIdForBasePrices',
];

function parseCsv(text) {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const header = (lines.shift() || '').split(',').map((h) => h.trim());
  const rows = lines.map((line) => {
    const cols = line.split(',').map((c) => c.trim());
    const obj = {};
    header.forEach((h, i) => (obj[h] = cols[i] || ''));
    return obj;
  });
  return { header, rows };
}

// Centralized builder for error CSV rows (frontend invalids + backend failures)
function buildErrorRows({ invalids = [], failures = [], parsedRows = [] }) {
  const rows = [];
  invalids.forEach((invalid) => {
    rows.push({
      lineNumber: invalid.lineNumber,
      ...invalid,
      error: Array.isArray(invalid.errors)
        ? invalid.errors.join('; ')
        : invalid.errors,
    });
  });
  failures.forEach((f) => {
    const match = parsedRows.find((row) => row.input?.schemaId === f.schemaId);
    const base = match
      ? { lineNumber: match.lineNumber, ...match.raw }
      : { schemaId: f.schemaId };
    rows.push({
      ...base,
      error: Array.isArray(f.errors) ? f.errors.join('; ') : f.errors,
    });
  });
  return rows;
}

const SchemaTemplatesCSVTool = () => {
  const BACKEND_ERROR_MAP = {
    PRICE_DISPLAY_INVALID:
      'priceDisplayType is required and must be EXPLICIT or IMPLICIT',
    TIME_WINDOWS_REQUIRED:
      'timeBound.timeWindows is required and must be a non-empty list',
  };

  // Mode state for switching between Update and Create operations
  const [mode, setMode] = useState('update');

  // Reset form when mode changes to ensure clean state
  const handleModeChange = (newMode) => {
    setMode(newMode);
    // Don't reset form immediately to allow table to re-render with new mode
    // Only reset if there are parsed rows
    if (parsedRows.length > 0) {
      resetForm();
    }
  };
  function mapBackendMessage(errLike) {
    const code = errLike?.extensions?.code || errLike?.code;
    if (code && BACKEND_ERROR_MAP[code]) return BACKEND_ERROR_MAP[code];
    const message = typeof errLike === 'string' ? errLike : errLike?.message;
    if (!message) return '';
    const lower = message.toLowerCase();
    if (lower.includes('pricedisplaytype'))
      return BACKEND_ERROR_MAP.PRICE_DISPLAY_INVALID;
    if (lower.includes('timewindows'))
      return BACKEND_ERROR_MAP.TIME_WINDOWS_REQUIRED;
    if (lower.includes('coerced null value')) {
      return 'A required field is missing. Check required columns for this template type.';
    }
    if (
      lower.includes('timeboundscheduletemplateinput') &&
      lower.includes('not defined')
    ) {
      return 'Invalid field for timeBound. Allowed fields: discount, priceDisplay, timeWindows.';
    }
    if (
      lower.includes('updateschematemplatesinput') &&
      lower.includes('not defined')
    ) {
      return 'Invalid root field. Use nested expiring/timeBound/subsidy; do not send root-level discount.';
    }
    if (lower.includes('day_of_week')) {
      return 'Use timeWindows entries with camelCase keys: dayOfWeek, startTime, endTime.';
    }
    return message;
  }
  const [csvFile, setCsvFile] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedRows, setParsedRows] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [invalidRows, setInvalidRows] = useState([]);
  const [downloadErrors, setDownloadErrors] = useState([]);
  const csvFileRef = useRef(null);

  function downloadErrorCsv() {
    if (downloadErrors.length === 0) return;
    const columns = [
      'lineNumber',
      'schemaId',
      'schemaType',
      'discount',
      'sharedSubsidyClientDiscount',
      'discountSchedule',
      'discountUI',
      'expiringDays',
      'priceDisplayType',
      'platform',
      ...(mode === 'create' ? ['replicaSchemaIdForBasePrices'] : []),
      'error',
    ];
    // Escapes values for safe CSV export by handling special characters
    const escape = (value) => {
      // Convert null/undefined to empty string, everything else to string
      const stringValue =
        value === undefined || value === null ? '' : String(value);

      // Check if value contains CSV special characters (comma, quote, newline)
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        // CSV escaping: wrap in quotes and escape internal quotes by doubling them
        return '"' + stringValue.replace(/"/g, '""') + '"';
      }

      // Safe to return as-is if no special characters
      return stringValue;
    };
    const lines = [columns.join(',')].concat(
      downloadErrors.map((row) => columns.map((c) => escape(row[c])).join(',')),
    );
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schema-template-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const resetForm = (keepFeedback = false) => {
    setCsvFile(null);
    setParsedRows([]);
    setRowErrors({});
    setInvalidRows([]);
    if (!keepFeedback) setFeedback(null);
    setUploadProgress(0);
    if (csvFileRef.current) csvFileRef.current.value = null;
  };

  function validateAndParse(text) {
    setFeedback(null);
    const { header, rows } = parseCsv(text);

    // Use different required headers based on mode
    const requiredHeaders =
      mode === 'create' ? CREATE_MODE_REQUIRED_HEADERS : REQUIRED_HEADERS;
    const missing = requiredHeaders.filter((h) => !header.includes(h));
    if (missing.length) {
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: 'Header Validation',
            description: `Missing columns: ${missing.join(', ')}${
              mode === 'create'
                ? ' (Create mode requires replicaSchemaIdForBasePrices)'
                : ''
            }`,
          },
        ],
      });
      setParsedRows([]);
      setRowErrors({});
      setInvalidRows([]);
      return;
    }

    const newRows = [];
    const errorsByIndex = {};
    const invalids = [];
    rows.forEach((r, idx) => {
      const { input, errors } = buildUpdateInputRow(r, mode);

      // Additional validation for Create mode: replicaSchemaIdForBasePrices is required
      if (mode === 'create') {
        const replicaSchemaId = String(
          r.replicaSchemaIdForBasePrices || '',
        ).trim();
        if (!replicaSchemaId) {
          errors.push(
            'replicaSchemaIdForBasePrices is required in Create mode',
          );
        }
      }

      newRows.push({ lineNumber: idx + 2, raw: r, input });
      if (errors.length) {
        errorsByIndex[idx] = errors;
        invalids.push({ lineNumber: idx + 2, ...r, errors });
      }
    });

    // Duplicate protection: block duplicate schemaId entries (esp. with mixed templateType)
    const bySchema = new Map();
    newRows.forEach((row, idx) => {
      const id = String(row.raw.schemaId || '').trim();
      if (!id) return;
      const type = String(
        row.input?.templateType || row.raw.schemaType || '',
      ).trim();
      if (!bySchema.has(id)) bySchema.set(id, []);
      bySchema.get(id).push({ idx, lineNumber: row.lineNumber, type });
    });
    bySchema.forEach((entries) => {
      if (entries.length <= 1) return;
      const typeSet = new Set(entries.map((e) => e.type).filter(Boolean));
      const msg =
        typeSet.size > 1
          ? `Duplicate schemaId with mixed template types (${[...typeSet].join(
              ', ',
            )})`
          : 'Duplicate schemaId found multiple times in CSV';
      entries.forEach(({ idx, lineNumber }) => {
        errorsByIndex[idx] = [...(errorsByIndex[idx] || []), msg];
        if (!invalids.find((invalid) => invalid.lineNumber === lineNumber)) {
          invalids.push({ lineNumber, ...newRows[idx].raw, errors: [msg] });
        }
      });
    });
    setParsedRows(newRows);
    setRowErrors(errorsByIndex);
    setInvalidRows(invalids);
    if (Object.keys(errorsByIndex).length > 0) {
      setDownloadErrors(
        buildErrorRows({ invalids, failures: [], parsedRows: newRows }),
      );
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: 'Fix CSV errors before upload',
            description: `${invalids.length} row(s) have errors.`,
          },
        ],
      });
      return;
    }
    setDownloadErrors([]);
    setUploadProgress(30);
  }

  function onFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadProgress(10);
      validateAndParse(ev.target.result);
      setCsvFile(file);
    };
    reader.readAsText(file);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!csvFile) return;
    const validInputs = parsedRows
      .map((r, idx) => ({ idx, input: r.input }))
      .filter(({ idx }) => !(idx in rowErrors))
      .map(({ input }) => input);

    if (validInputs.length === 0 || invalidRows.length > 0) {
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: 'Fix CSV errors before upload',
            description:
              validInputs.length === 0
                ? 'No valid rows found.'
                : `${invalidRows.length} row(s) have errors. This upload is all-or-nothing; please correct them and re-upload.`,
          },
        ],
      });
      return;
    }

    setLoading(true);
    setUploadProgress(30);
    try {
      // Prefer batch API for all-or-nothing semantics
      setUploadProgress(70);

      // Branch on mode to call appropriate mutation
      const batch =
        mode === 'create'
          ? await CreateSchemaTemplatesBatch(validInputs)
          : await UpdateSchemaTemplatesBatch(validInputs);
      const successes = [];
      const failures = [];
      if (batch?.results?.length) {
        batch.results.forEach((result, idx) => {
          if (result.success)
            successes.push({
              schemaId: result.schemaId,
              message: result.message,
            });
          else
            failures.push({
              schemaId: result.schemaId,
              errors:
                result.errors && result.errors.length
                  ? result.errors
                  : [result.message || 'Unknown error'],
              index: result.index ?? idx,
            });
        });
      } else if (batch?.success === false) {
        failures.push({
          schemaId: 'BATCH',
          errors: [batch.message || 'Batch failed'],
        });
      }
      setUploadProgress(100);
      setLoading(false);

      const hasErrors = failures.length > 0;
      const hasSuccesses = successes.length > 0;
      const hasFrontendInvalids = invalidRows.length > 0;

      if (hasErrors || hasFrontendInvalids) {
        // Prepare downloadable error rows
        const errorRows = buildErrorRows({
          invalids: invalidRows,
          failures,
          parsedRows,
        });
        setDownloadErrors(errorRows);
        const dataSections = [];
        if (hasFrontendInvalids) {
          dataSections.push({
            title: 'Frontend validation errors',
            description: 'Some rows were invalid before calling the API.',
            details: invalidRows
              .map(
                (invalidRow) =>
                  `${invalidRow.lineNumber}: ${
                    invalidRow.schemaId || invalidRow.raw?.schemaId || ''
                  } → ${invalidRow.errors.join('; ')}`,
              )
              .join('\n'),
          });
        }
        if (hasErrors) {
          dataSections.push({
            title: 'Backend validation errors',
            description: 'Some rows failed on the backend.',
            details: failures
              .map(
                (failure) =>
                  `${failure.schemaId}: ${
                    Array.isArray(failure.errors)
                      ? failure.errors.map(mapBackendMessage).join('; ')
                      : mapBackendMessage(failure.errors)
                  }`,
              )
              .join('\n'),
          });
          if (hasSuccesses) {
            dataSections.push({
              title: 'Successfully updated',
              description: `${successes.length} template(s) updated`,
              details: successes
                .map((s) => `${s.schemaId}: ${s.message || 'Updated'}`)
                .join('\n'),
            });
          }
        }
        // Already built above

        // Enforce all-or-nothing UX: if any error, do not proceed
        setFeedback({ type: VALIDATION_TYPE.ERRORS, data: dataSections });
        return;
      }

      setFeedback({
        type: VALIDATION_TYPE.SUCCESS,
        data: [
          hasFrontendInvalids
            ? {
                title: 'Frontend validation errors',
                description: `${invalidRows.length} row(s) skipped before upload`,
                details: invalidRows
                  .map(
                    (invalidRow) =>
                      `${invalidRow.lineNumber}: ${
                        invalidRow.schemaId || invalidRow.raw?.schemaId || ''
                      } → ${invalidRow.errors.join('; ')}`,
                  )
                  .join('\n'),
              }
            : null,
          {
            title:
              mode === 'create'
                ? 'Schema Templates Created'
                : 'Schema Templates Updated',
            description: `Processed ${validInputs.length} templates: ${successes.length} succeeded, ${failures.length} failed.`,
            details: hasSuccesses
              ? successes
                  .map(
                    (s) =>
                      `${s.schemaId}: ${
                        s.message || (mode === 'create' ? 'Created' : 'Updated')
                      }`,
                  )
                  .join('\n')
              : undefined,
          },
        ].filter(Boolean),
      });
      // Keep the success banner visible while clearing the form inputs
      resetForm(true);
    } catch (err) {
      setUploadProgress(100);
      setLoading(false);
      const backendMsgs = Array.isArray(err?.errors)
        ? err.errors.map((e) => e?.message).filter(Boolean)
        : Array.isArray(err?.response?.errors)
        ? err.response.errors.map((e) => e?.message).filter(Boolean)
        : [];
      const description = backendMsgs.length
        ? backendMsgs.map(mapBackendMessage).join('\n')
        : mapBackendMessage(err?.message) || JSON.stringify(err);
      setFeedback({
        type: VALIDATION_TYPE.ERRORS,
        data: [
          {
            title: mode === 'create' ? 'Create failed' : 'Update failed',
            description,
          },
        ],
      });
    }
  }

  const invalidCount = invalidRows.length;
  const validCount = parsedRows.length - invalidCount;

  return (
    <main className={styles.container}>
      {feedback ? (
        <div aria-live="polite" role="status">
          <ValidationFeedback data={feedback.data} type={feedback.type} />
        </div>
      ) : null}
      <h1>Fridge Schema CSV Upload</h1>

      {/* Mode toggle */}
      <div className={styles.modeToggle}>
        <label htmlFor="modeSelect">Operation Mode:</label>
        <select
          id="modeSelect"
          value={mode}
          onChange={(e) => handleModeChange(e.target.value)}
          disabled={loading}
          className={styles.modeSelect}
        >
          <option value="update">Update Existing Templates</option>
          <option value="create">Create New Templates</option>
        </select>
      </div>

      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.formWrapper}>
          <div className={styles.flexColumn}>
            <label htmlFor="csvUpload">Upload CSV file</label>
            <span className={styles.fileInfo}>
              Columns: schemaId, schemaType, discount,
              sharedSubsidyClientDiscount, discountSchedule, discountUI,
              expiringDays, priceDisplayType, platform
              {mode === 'create' && ', replicaSchemaIdForBasePrices'}
              {mode === 'create' && ' (Create mode: will create new templates)'}
            </span>
            <input
              id="csvUpload"
              type="file"
              accept=".csv"
              onChange={onFileChange}
              ref={csvFileRef}
              style={{ cursor: 'pointer' }}
              disabled={loading}
            />
          </div>
          {invalidRows.length > 0 ? (
            <button
              className={styles.button}
              onClick={downloadErrorCsv}
              type="button"
            >
              Download All Invalid Rows (
              {downloadErrors.length || invalidRows.length || 0})
            </button>
          ) : (
            <button
              className={styles.button}
              type="submit"
              disabled={!csvFile || loading || invalidRows.length > 0}
              title={
                invalidRows.length > 0
                  ? 'Fix CSV errors before upload (all-or-nothing)'
                  : ''
              }
            >
              {loading
                ? 'Loading...'
                : mode === 'create'
                ? 'Create Templates'
                : 'Update Templates'}
            </button>
          )}
        </div>
      </form>

      {loading && (
        <div className={styles.progressBarContainer}>
          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
        </div>
      )}

      {parsedRows.length > 0 && (
        <div className={styles.tableContainer}>
          <h3>CSV Data Preview</h3>
          <div className={styles.summary}>
            <p>
              <strong>Summary:</strong> {parsedRows.length} total rows,{' '}
              <span
                className={
                  validCount > 0
                    ? styles.summaryCountValid
                    : styles.summaryCountNeutral
                }
              >
                {validCount} valid
              </span>
              ,{' '}
              <span
                className={
                  invalidCount > 0
                    ? styles.summaryCountInvalid
                    : styles.summaryCountNeutral
                }
              >
                {invalidCount} invalid
              </span>
            </p>
          </div>
          <div className={styles.tableWrapper}>
            <Table key={`table-${mode}`}>
              <thead>
                <tr>
                  <th>Line #</th>
                  <th>Status</th>
                  <th>schemaId</th>
                  <th>schemaType</th>
                  <th>discount</th>
                  <th>clientDiscount</th>
                  <th>discountSchedule</th>
                  <th>discountUI</th>
                  <th>expiringDays</th>
                  <th>priceDisplayType</th>
                  <th>platform</th>
                  {mode === 'create' && <th>replicaSchemaIdForBasePrices</th>}
                  <th>Validation</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, idx) => {
                  const errors = rowErrors[idx] || [];
                  const isInvalid = errors.length > 0;
                  const isDuplicate = errors.some((e) =>
                    String(e).toLowerCase().includes('duplicate'),
                  );
                  return (
                    <tr
                      key={idx}
                      className={isInvalid ? styles.errorRow : styles.validRow}
                    >
                      <td>{row.lineNumber}</td>
                      <td>
                        {isInvalid ? (
                          <Badge bg="danger">
                            {isDuplicate ? 'Duplicate' : 'Invalid'}
                          </Badge>
                        ) : (
                          <Badge bg="success">Valid</Badge>
                        )}
                      </td>
                      <td>{row.raw.schemaId}</td>
                      <td>{row.raw.schemaType}</td>
                      <td>{row.raw.discount}</td>
                      <td>{row.raw.sharedSubsidyClientDiscount || '-'}</td>
                      <td>{row.raw.discountSchedule}</td>
                      <td>{row.raw.discountUI}</td>
                      <td>{row.raw.expiringDays}</td>
                      <td>{row.raw.priceDisplayType}</td>
                      <td>{row.raw.platform}</td>
                      {mode === 'create' && (
                        <td>{row.raw.replicaSchemaIdForBasePrices}</td>
                      )}
                      <td>
                        {errors.length > 0 ? (
                          <ul className={styles.errorList}>
                            {errors.map((e, i) => (
                              <li key={i} className={styles.errorItem}>
                                {e}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className={styles.validText}> Valid</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </main>
  );
};

export default SchemaTemplatesCSVTool;
